import classes from "./PlatformTable.module.css";
import useTheme from "../../../../common/hooks/useTheme";
import InfoIcon from "../../svg/InfoIcon";
import CustomTooltip from "../common/CustomTooltip";
import React from "react";

type Props = {
  data: any;
  insightData?: any;
  title?: string;
  dataPointColor?: string;
  hasSmallFontInTitle?: boolean;
  hasUnevenColumns?: boolean;
  details?: any;
  type?: number;
};
const PlatformTable = ({
  data,
  insightData,
  title,
  dataPointColor,
  hasSmallFontInTitle,
  hasUnevenColumns,
  details,
  type,
}: Props) => {
  const theme = useTheme();

  const headerArray: string[] = Object.values(data[0]);

  const rowArrays = data.slice(1).map((item: any) => Object.values(item));

  const calculateFlexValue = (
    hasUnevenColumns: boolean | undefined,
    index: number
  ): number => {
    if (!hasUnevenColumns) return 1;
    switch (index) {
      case 0:
        return 3;
      case 1:
      case 2:
        return 2;
      default:
        return 1;
    }
  };

  const getTooltipContentFormatter = (data: any, index: number) => {
    if (!data) return "";

    // The field to display depends on the itemType (c5, c6, or c7)
    const field = data.field;
    const value = data.value;

    // Format and return content based on index
    if (index === 0 || index % 2 === 0) {
      // This is a header/title (will be bold)
      return field || "Summary";
    } else {
      // This is insight data (will be red)
      return value || "No insight available";
    }
  };

  const prepareTooltipData = (rowIndex: number) => {
    if (!insightData) return [];

    // Create an array with headers and values for c5, c6, and c7
    const result = [];

    // Add c5 header and data
    if (insightData[0]?.c5) {
      result.push({ field: insightData[0].c5, value: null });
      result.push({ field: null, value: insightData[rowIndex]?.c5 });
    }

    // Add c6 header and data
    if (insightData[0]?.c6) {
      result.push({ field: insightData[0].c6, value: null });
      result.push({ field: null, value: insightData[rowIndex]?.c6 });
    }

    // Add c7 header and data
    if (insightData[0]?.c7) {
      result.push({ field: insightData[0].c7, value: null });
      result.push({ field: null, value: insightData[rowIndex]?.c7 });
    }

    return result;
  };

  return (
    <div className={classes.container}>
      {title && (
        <div
          className={`${classes.titleContainer} ${
            hasSmallFontInTitle ? classes.smallTitleContainer : ""
          }`}
        >
          {dataPointColor && (
            <div
              className={classes.dataPoint}
              style={{ backgroundColor: dataPointColor }}
            />
          )}
          <div
            className={hasSmallFontInTitle ? classes.smallTitle : classes.title}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
          >
            {title}
          </div>
        </div>
      )}
      {details && (
        <div style={styles.detailContainer(theme)}>
          <div>
            <div style={styles.detailTitle(theme)}>{details.title}</div>
          </div>
          <div style={styles.detailInner}>
            {type === 2 && (
              <div style={styles.detailItem(theme, type ?? 0)}>
                {details.sub_type}
              </div>
            )}
            <div style={styles.detailItem(theme, type ?? 0)}>
              {details.count}
            </div>
            <div style={styles.detailItem(theme, type ?? 0)}>
              {details.duration}
            </div>
            <div style={styles.detailItem(theme, type ?? 0)}>
              {details.kcal}
            </div>
          </div>
        </div>
      )}
      <div
        className={classes.tableContainer}
        style={{
          borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
          backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
        }}
      >
        <div
          className={classes.tableRow}
          style={{
            color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
            border: "none",
            padding: "0px 16px 4px",
          }}
        >
          {headerArray.map((el: string, index: number) => (
            <div
              key={index}
              className={classes.tableHeaderCell}
              style={{
                flex: calculateFlexValue(hasUnevenColumns, index),
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              }}
            >
              {el}
            </div>
          ))}
        </div>
        {rowArrays.map((item: string[], rowIndex: number) => (
          <div
            key={rowIndex}
            className={classes.tableRow}
            style={{
              borderTopColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
            }}
          >
            {item.map((el: string, index: number) => (
              <div
                key={index}
                className={classes.tableCell}
                style={{
                  flex: calculateFlexValue(hasUnevenColumns, index),
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                }}
              >
                {index === 0 && insightData && (
                  <CustomTooltip
                    label={`${item[0]} insights`}
                    type={"large"}
                    customHeight={"20px"}
                    tooltipData={prepareTooltipData(rowIndex + 1)}
                    getTooltipContent={getTooltipContentFormatter}
                  >
                    <InfoIcon size={10} />
                  </CustomTooltip>
                )}
                {el}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlatformTable;

const styles = {
  detailContainer: (theme: any) => ({
    marginBottom: "16px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "8px 16px",
    backgroundColor: "#fff",
  }),
  detailTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    marginBottom: "8px",
  }),
  detailInner: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  detailItem: (theme: any, type: number) => ({
    padding: "4px 8px",
    borderRadius: "8px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    backgroundColor:
      type === 3
        ? theme.colorTokens.D[10]
        : type === 1
        ? theme.colorTokens.W[10]
        : theme.colorTokens.P[10],
  }),
};
